import {
  Shield,
  FileText,
  Users,
  CheckCircle,
  BookOpen,
  Heart,
  Sparkles,
  Target,
  Scale,
  AlertCircle,
  GraduationCap,
  Building,
} from "lucide-react";

export default function GovernancePage() {
  const governancePillars = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: "Governance Constitution Framework",
      description:
        "Comprehensive constitutional guidelines for hostel management",
    },
    {
      icon: <Scale className="h-8 w-8" />,
      title: "Trustee Code of Ethics",
      description: "Ethical standards and conduct guidelines for all trustees",
    },
    {
      icon: <AlertCircle className="h-8 w-8" />,
      title: "Conflict of Interest Rules",
      description:
        "Clear protocols to prevent and manage conflicts of interest",
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Decision-Making Systems",
      description: "Structured processes for transparent decision-making",
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Committee Architecture",
      description: "Well-defined committee structures and responsibilities",
    },
    {
      icon: <FileText className="h-8 w-8" />,
      title: "Documentation Protocols",
      description: "Standardized documentation and record-keeping systems",
    },
    {
      icon: <CheckCircle className="h-8 w-8" />,
      title: "SIRC Compliance",
      description: "Social Impact Rating & Compliance framework adherence",
    },
  ];

  const committees = [
    {
      title: "Governance & Compliance Committee",
      icon: <Shield className="h-10 w-10" />,
      description:
        "Institutional bylaws, policy framework, Compliance with Section 8 norms, Standard Operating Protocols (SOPs)",
      color: "from-[#7A1E1E] to-[#8B2E2E]",
    },
    {
      title: "Finance, Audit & Risk Management Committee",
      icon: <FileText className="h-10 w-10" />,
      description:
        "Budgeting, Internal audit systems, Risk mitigation, Financial transparency & reporting",
      color: "from-[#D4A017] to-[#B8900F]",
    },
    {
      title: "Infrastructure Modernisation & Smart Campus Committee",
      icon: <Building className="h-10 w-10" />,
      description:
        "Campus redevelopment, Hostel retrofitting, Digital classrooms, Safety & disaster-readiness audits",
      color: "from-[#7A1E1E] to-[#8B2E2E]",
    },
    {
      title: "Event Management, PR & Federation Outreach Committee",
      icon: <Users className="h-10 w-10" />,
      description:
        "National & state-level JBF events, Media relations, Trustee networking, Annual JBF Conclave",
      color: "from-[#D4A017] to-[#B8900F]",
    },
    {
      title: "Legal Affairs, Contracts & Institutional Advisory Committee",
      icon: <AlertCircle className="h-10 w-10" />,
      description:
        "MOUs, contracts, agreements, Legal compliance, Advisory for individual institutions, Support for land, construction & charity issues",
      color: "from-[#7A1E1E] to-[#8B2E2E]",
    },
    {
      title: "Special Projects, Expansion & New Hostels Development Committee",
      icon: <Target className="h-10 w-10" />,
      description:
        "New institution onboarding, National expansion roadmap, Strategic interventions in weak hostels, Model campuses",
      color: "from-[#D4A017] to-[#B8900F]",
    },
  ];

  const jainIntegration = [
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Dietary Purity Norms",
      description:
        "Strict adherence to Jain dietary principles and food preparation standards",
    },
    {
      icon: <Sparkles className="h-8 w-8" />,
      title: "Prayer/Meditation Periods",
      description: "Dedicated time for spiritual practices and contemplation",
    },
    {
      icon: <GraduationCap className="h-8 w-8" />,
      title: "Cultural Festivals",
      description: "Celebration of Jain festivals and cultural traditions",
    },
    {
      icon: <BookOpen className="h-8 w-8" />,
      title: "Language & Spiritual Guidance",
      description: "Teaching Jain philosophy, values, and spiritual wisdom",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1709803857154-d20ee16ff763?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsZWFkZXJzaGlwJTIwZ292ZXJuYW5jZSUyMGJvYXJkfGVufDF8fHx8MTc2NTE5NTk2Mnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Leadership governance"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto mb-10 mt-10 max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">JBF Governance Model</h1>
            {/* <p className="text-2xl leading-relaxed text-gray-100">
              Built on the S-ARDA framework in partnership with Mehta Atre
              Associates, ensuring world-class governance and transparency.
            </p> */}
          </div>
        </div>
      </section>

      {/* Why Governance Comes First */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Why Governance Comes First
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-xl text-gray-700">
              Without governance discipline, no hostel can progress. MAA
              provides JBF with:
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {governancePillars.map(pillar => (
              <div
                key={pillar.title}
                className="rounded-xl border-l-4 border-[#D4A017] bg-white p-6 shadow-md transition-all hover:shadow-xl"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                  {pillar.icon}
                </div>
                <h4 className="mb-2 text-lg text-[#7A1E1E]">{pillar.title}</h4>
                <p className="text-sm text-gray-700">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reconstruction Blueprint */}
      <section className="bg-[#FFF5E6] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Reconstruction Blueprint (SARB-JBF Version)
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              A comprehensive committee structure ensuring holistic governance
              and student welfare.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {committees.map(committee => (
              <div
                key={committee.title}
                className="transform overflow-hidden rounded-xl bg-white shadow-lg transition-all hover:-translate-y-2 hover:shadow-2xl"
              >
                <div
                  className={`bg-gradient-to-br ${committee.color} p-6 text-center text-white`}
                >
                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    {committee.icon}
                  </div>
                  <h3 className="text-xl">{committee.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-center text-gray-700">
                    {committee.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jain Value Integration */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Jain Value Integration
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Governance that honors and upholds Jain cultural values and
              traditions.
            </p>
          </div>

          <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {jainIntegration.map(item => (
              <div
                key={item.title}
                className="rounded-xl border-2 border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-[#FFF5E6] p-6 text-center shadow-lg transition-all hover:shadow-xl"
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                  {item.icon}
                </div>
                <h3 className="mb-3 text-lg text-[#7A1E1E]">{item.title}</h3>
                <p className="text-sm text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-gradient-to-r from-[#7A1E1E] to-[#8B2E2E] p-8 text-center text-white">
            <Sparkles className="mx-auto mb-4 h-12 w-12 text-[#D4A017]" />
            <h3 className="mb-4 text-2xl">Cultural Integrity Guaranteed</h3>
            <p className="mx-auto max-w-2xl text-lg text-gray-100">
              All governance frameworks are designed to strengthen, not
              compromise, Jain identity, principles, culture, and values. Our
              commitment is to excellence with authenticity.
            </p>
          </div>
        </div>
      </section>

      {/* Adopt World-Class Governance CTA Section */}
      <section className="bg-white py-8">
        <div className="bg-[#7A1E1E] py-16">
          <div className="mx-auto max-w-4xl px-4 text-center text-white sm:px-6 lg:px-8">
            <h2 className="mb-6 text-4xl font-bold sm:text-5xl">
              Adopt World-Class Governance
            </h2>
            <p className="mb-8 text-xl leading-relaxed text-gray-100">
              Transform your institution with proven governance frameworks that
              ensure transparency, accountability, and sustainable growth.
            </p>
            <button className="rounded-lg bg-[#D4A017] px-8 py-4 font-semibold text-white transition-all hover:bg-[#B8900F]">
              Learn More About S-ARDA
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
